import { DISPLAYED_CALENDAR_TABLE_VIEW } from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_table/displayedCalendarTableView';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { ArrowDownIcon, ArrowUpIcon, Bars3Icon } from '@heroicons/react/24/outline';
import Drawer from '@ui/drawer/Drawer';
import useDrawer from '@ui/drawer/DrawerRef';
import FormButton from '@ui/button/FormButton';
import FormCheckbox from '@ui/checkbox/FormCheckbox';
import Spinner from '@ui/spinner/Spinner';
import { PmsScope, usePmsMetrics, pmsScope, pmsPoint } from '@app/model/pricing-automation/service/PmsMetrics';
import { useMarketStateFilterInputTools } from '@app/site/secured/rateshopper/rates/provider/useMarketStateFilterInputTools';
import HotelRoomMappingType from '@app/model/market_state/HotelRoomMappingType';
import { useLocation } from 'react-router-dom';
import { CalendarTableColumn } from '@app/model/company_hotel/settings/CalendarTableSettings';
import { useSaveCompanyHotelCalendarTableSettingsMutation } from '@app/model/company_hotel/service/CompanyHotelSettingsServiceHooks';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import { useMarketStateTools } from '../../common/hook/useMarketStateTools';
import { usePricingRights } from '@util/PricingRightsHook';
const METRICS = ['REC_PRICE', 'PICKUP', 'AVAILABILITY', 'MEDIAN'];
const LABELS: Record<string, string> = { REC_PRICE: 'Rec Price', PICKUP: 'Pickup', AVAILABILITY: 'Availability', MEDIAN: 'Median' };

type Column = CalendarTableColumn & { label: string; optional: boolean };
type Context = { columns: Column[]; visible: Column[]; availability: Map<string, PmsScope[]>; loading: boolean; error: boolean; openColumnOrder: () => void };
const ColumnsContext = createContext<Context>({ columns: [], visible: [], availability: new Map(), loading: false, error: false, openColumnOrder: () => undefined });
export const useDateTableColumns = () => useContext(ColumnsContext);

export const DateTableColumnsProvider: React.FunctionComponent<React.PropsWithChildren> = ({ children }) => {
    const { companyHotel } = useSelectedCompanyHotel();
    const { filter } = useMarketStateFilterInputTools();
    const visibleIds = new Set(companyHotel.visibleCompetitors(filter.competitorTagIds).map(c => `competitor:${c.id}`));
    const { canViewRecommendations } = usePricingRights(companyHotel);
    const tools = useMarketStateTools();
    const drawer = useDrawer();
    const location = useLocation();
    const isDateTable = location.pathname.endsWith("/table") && DISPLAYED_CALENDAR_TABLE_VIEW === "CLASSIC";
    const [saveError, setSaveError] = useState(false);
    const [dragKey, setDragKey] = useState<string | null>(null);
    const defaults = useMemo<Column[]>(() => [
        ...METRICS.map(key => ({ key, label: key === 'MEDIAN' ? companyHotel.comparisonLabel() : LABELS[key], visible: true, optional: true })),
        ...(companyHotel.competitors ?? []).map(c => ({ key: `competitor:${c.id}`, label: c.name, visible: true, optional: false })),
    ], [companyHotel.competitors, companyHotel.settings.calendar.comparisonMethod]);
    const saved = companyHotel.settings.calendarTable.columns;
    const resolved = useMemo(() => {
        const byKey = new Map(defaults.map(c => [c.key, c]));
        const result: Column[] = [];
        for (const c of saved ?? []) {
            const column = byKey.get(c.key);
            if (column !== undefined) {
                result.push({ ...column, visible: column.optional ? c.visible : true });
                byKey.delete(c.key);
            }
        }
        return [...result, ...byKey.values()];
    }, [defaults, saved]);
    const [columns, setColumns] = useState(resolved);
    useEffect(() => setColumns(resolved), [resolved]);
    const allowed = (c: Column) => canViewRecommendations || (c.key !== 'REC_PRICE' && c.key !== 'PICKUP');
    const visible = columns.filter(c => c.visible && allowed(c) && (c.optional || visibleIds.has(c.key)));
    const save = useSaveCompanyHotelCalendarTableSettingsMutation({
        onError: () => { setColumns(resolved); setSaveError(true); },
    });
    const persist = (next: Column[]) => {
        if (save.isPending) return;
        setSaveError(false);
        setColumns(next);
        save.mutate({ companyHotelId: companyHotel.id, input: {
            ...companyHotel.settings.calendarTable,
            columns: next.map(({ key, visible }) => ({ key, visible })),
        } });
    };
    const move = (key: string, target: number) => {
        const from = columns.findIndex(c => c.key === key);
        if (from < 0 || target < 0 || target >= columns.length || from === target) return;
        const next = [...columns];
        const [column] = next.splice(from, 1);
        next.splice(target, 0, column);
        persist(next);
    };
    const availability = usePmsMetrics(companyHotel.id, tools.calendarMonth.index.year, tools.calendarMonth.index.month, isDateTable);
    const value = { columns, visible, availability: new Map((availability.data?.days ?? []).map(row => [row.statDate, row.scopes])),
        loading: availability.isLoading, error: availability.isError,
        openColumnOrder: () => drawer.open() };
    return <ColumnsContext.Provider value={value}>
        <Drawer title="Column order" size="normal" ref={drawer.ref}>
            <div className="space-y-4 px-6 py-5">
                <p className="text-sm text-gray-600">Drag a row or use the arrows to change the order. Untick a metric to hide it.</p>
                <p className="text-xs text-gray-500">Date and your hotel price stay at the start. Changes apply to this hotel's date table.</p>
                {saveError && <p role="alert" className="text-sm text-red-700">The change could not be saved. The previous layout is restored. Try again.</p>}
                <ol className="divide-y divide-gray-200">
                    {columns.map((column, index) => <li key={column.key} data-column-key={column.key}
                        draggable={!save.isPending} onDragStart={() => setDragKey(column.key)} onDragEnd={() => setDragKey(null)}
                        onDragOver={event => event.preventDefault()} onDrop={event => { event.preventDefault(); if (dragKey !== null) move(dragKey, index); setDragKey(null); }}
                        className="flex items-center gap-2 py-3">
                        <Bars3Icon aria-hidden="true" className="h-4 w-4 shrink-0 cursor-grab text-gray-400" />
                        <div className="min-w-0 flex-1">
                            {column.optional ? <FormCheckbox name={column.key} label={column.label} checked={column.visible}
                                disabled={save.isPending || !allowed(column)} onChange={() => persist(columns.map(c => c.key === column.key ? { ...c, visible: !c.visible } : c))} />
                                : <span className="block truncate text-sm text-gray-700" title={column.label}>{column.label}</span>}
                            {!allowed(column) && <span className="text-xs text-gray-500">Requires recommendation access</span>}
                        </div>
                        <FormButton size="small" state="white" disabled={index === 0 || save.isPending} onClick={() => move(column.key, index - 1)}>
                            <ArrowUpIcon aria-hidden="true" className="h-4 w-4" /><span className="sr-only">Move {column.label} up</span>
                        </FormButton>
                        <FormButton size="small" state="white" disabled={index === columns.length - 1 || save.isPending} onClick={() => move(column.key, index + 1)}>
                            <ArrowDownIcon aria-hidden="true" className="h-4 w-4" /><span className="sr-only">Move {column.label} down</span>
                        </FormButton>
                    </li>)}
                </ol>
                <div className="flex items-center justify-between gap-2">
                    <FormButton size="small" state="white" disabled={save.isPending} onClick={() => persist(defaults)}>Reset columns</FormButton>
                    <span aria-live="polite" className="text-xs text-gray-500">{save.isPending ? 'Save in progress…' : 'Changes save automatically'}</span>
                </div>
            </div>
        </Drawer>
        {children}

    </ColumnsContext.Provider>;
};

export const DateTableOrderedCells: React.FunctionComponent<{
    recommendation: React.ReactNode; pickup: React.ReactNode; availability: React.ReactNode; median: React.ReactNode; competitors: React.ReactNode[];
}> = ({ recommendation, pickup, availability, median, competitors }) => {
    const { companyHotel } = useSelectedCompanyHotel();
    const { filter } = useMarketStateFilterInputTools();
    const { visible } = useDateTableColumns();
    const cells = new Map<string, React.ReactNode>([
        ['REC_PRICE', recommendation], ['PICKUP', pickup], ['AVAILABILITY', availability], ['MEDIAN', median],
        ...companyHotel.visibleCompetitors(filter.competitorTagIds).map((c, index) => [`competitor:${c.id}`, competitors[index]] as [string, React.ReactNode]),
    ]);
    return <>{visible.map(c => <React.Fragment key={c.key}>{cells.get(c.key)}</React.Fragment>)}</>;
};

export const AvailabilityCell: React.FunctionComponent<{ statDate: string; roomMappingType?: HotelRoomMappingType; className?: string }> = ({ statDate, roomMappingType, className = '' }) => {
    const { availability, loading, error } = useDateTableColumns();
    const row = availability.get(statDate);
    const scope = pmsScope(row, roomMappingType);
    const point = pmsPoint(scope);
    const count = point?.availability;
    const known = count != null;
    const title = known ? `PMS available rooms · all channels · latest snapshot ${point?.capturedAt ?? ''}` :
        [...(scope?.issues ?? []), ...(point?.issues ?? ['No PMS mapping or snapshot'])].join(', ');
    return <td className={`whitespace-nowrap border-r border-gray-200 px-2 py-1 text-center text-sm text-gray-700 ${className}`}
        data-availability-date={roomMappingType === undefined ? statDate : undefined}
        data-availability-stay-date={statDate} data-availability-room-type={roomMappingType}>
        {loading ? <Spinner /> : error ? <span className="text-red-600" title="Availability could not load">Error</span> :
            <span title={title}>{known ? count : '—'}</span>}
    </td>;
};
